import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { titleOf, taskCounts, STATUS_LABEL } from "@/pages/portal/portalUtils";
import { CheckCircle2, Circle, Clock, AlertTriangle, Star, Lightbulb, GitBranch, Sparkles, Flag, ShieldCheck } from "lucide-react";

export const CLASS_META = {
  official: { label: "Official Requirement", Icon: ShieldCheck, cls: "bg-[#013A5A] text-white border-[#013A5A]" },
  required: { label: "Required", Icon: Star, cls: "bg-sky-50 text-[#006090] border-sky-200" },
  recommended: { label: "Recommended", Icon: Lightbulb, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  conditional: { label: "Conditional", Icon: GitBranch, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  optional: { label: "Optional", Icon: Sparkles, cls: "bg-slate-50 text-slate-600 border-slate-200" },
  post_career: { label: "Post-Career", Icon: Flag, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const FILTERS = ["all", "official", "required", "recommended", "conditional", "optional", "post_career"];

const ClassBadge = ({ c, testId }) => {
  const m = CLASS_META[c];
  if (!m) return null;
  return <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide border rounded-full px-2 py-0.5 ${m.cls}`} data-testid={testId}><m.Icon className="w-3 h-3" />{m.label}</span>;
};

const stateOf = (t) => t.is_done ? { label: "Completed", Icon: CheckCircle2, cls: "text-emerald-600" }
  : t.is_planned ? { label: "Upcoming", Icon: Circle, cls: "text-slate-400" }
  : t.status === "needs_attention" ? { label: "Needs Attention", Icon: AlertTriangle, cls: "text-rose-600" }
  : { label: t.status === "submitted" ? "Submitted" : t.status === "in_progress" ? "In Process" : "Current", Icon: Clock, cls: "text-[#006090]" };

export default function PortalTrainingProgram({ goTo }) {
  const [tasks, setTasks] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    api.get("/portal/tasks").then((r) => setTasks(r.data.tasks || [])).catch(() => setTasks([]));
    api.get("/portal/roadmap").then((r) => setRoadmap(r.data)).catch(() => setRoadmap({}));
  }, []);
  if (!tasks || !roadmap) return <p className="text-sm text-slate-400">Loading your training program…</p>;
  const live = tasks.filter((t) => t.status !== "cancelled");
  const shown = live.filter((t) => filter === "all" || t.classification === filter);
  const phases = [...(roadmap.phases || []).map((p) => p.name), ...new Set(live.map((t) => t.phase).filter((n) => n && !(roadmap.phases || []).some((p) => p.name === n)))];
  const groups = [...phases.map((n) => ({ name: n, items: shown.filter((t) => t.phase === n) })),
    { name: "Other Program Tasks", items: shown.filter((t) => !t.phase) }].filter((g) => g.items.length);
  const proj = { done: live.filter((t) => t.is_done).length, total: live.length };
  return (
    <div className="space-y-6" data-testid="portal-training-program">
      <div className="rounded-2xl bg-[#013A5A] text-white p-6">
        <p className="text-[#5EEAD4] text-xs font-semibold uppercase tracking-wider">Career Conversion · Official Program Project</p>
        <p className="text-sm text-white/70 mt-1">Your full roadmap — every task in your program, including what's coming later.</p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          {[["Program Requirements", { done: roadmap.required_complete || 0, total: roadmap.required_total || 0 }, "tp-count-requirements"],
            ["Training Project", proj, "tp-count-project"], ["Your Tasks", taskCounts(live, "candidate"), "tp-count-mine"],
            ["District / Agent Tasks", taskCounts(live, "district"), "tp-count-district"]].map(([l, c, id]) => (
            <div key={id} className="rounded-xl bg-white/10 px-3 py-2" data-testid={id}><p className="text-[10px] uppercase tracking-wider text-white/60">{l}</p><p className="font-semibold">{c.done} of {c.total}</p></div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-white/60">Official program progress comes only from program requirements. Training tasks help you get there but don't change that number on their own.</p>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="tp-filters">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`tp-filter-${f}`} aria-pressed={filter === f}
            className={`text-xs rounded-full border px-3 py-1 ${filter === f ? "border-[#006090] bg-[#006090] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
            {f === "all" ? "All" : CLASS_META[f].label} <span className="opacity-70">{f === "all" ? live.length : live.filter((t) => t.classification === f).length}</span>
          </button>
        ))}
      </div>
      {groups.length === 0 ? <p className="text-sm text-slate-400" data-testid="tp-empty">No tasks in this category yet.</p> : groups.map((g) => (
        <section key={g.name} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`tp-phase-${g.name}`}>
          <h3 className="font-display font-semibold text-slate-900 mb-3">{g.name} <span className="text-xs font-medium text-slate-400">{g.items.filter((t) => t.is_done).length}/{g.items.length}</span></h3>
          <ul className="space-y-1.5">{g.items.map((t) => {
            const s = stateOf(t);
            const open = !t.read_only && !t.is_planned && t.owner === "candidate";
            return (
              <li key={t.id} data-testid={`tp-task-${t.id}`}>
                <button disabled={!open} onClick={() => open && goTo("tasks", null, { taskId: t.id })} className={`w-full text-left flex items-start gap-2 rounded-lg px-3 py-2 bg-slate-50 ${open ? "hover:bg-[#006090]/5" : "cursor-default"}`}>
                  <s.Icon className={`w-4 h-4 mt-0.5 shrink-0 ${s.cls}`} />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm ${t.is_done ? "text-slate-500" : "text-slate-800 font-medium"}`}>{titleOf(t)}</span>
                    <span className="text-[11px] text-slate-500">{t.owner === "district" ? (t.assignee_name ? `Assigned to ${t.assignee_name}` : t.owner_label) : "Your task"} · {s.label}{t.completed_by_district ? " · Completed by District Team" : ""}</span>
                  </span>
                  <ClassBadge c={t.classification} testId={`tp-class-${t.id}`} />
                </button>
              </li>
            );
          })}</ul>
        </section>
      ))}
    </div>
  );
}

export { STATUS_LABEL };
